import {Queue} from "./Queue";
import {DEFAULT_VOICE_INDEX, Output} from "./Configuration";
import {
    AudioConfig,
    AudioOutputStream,
    SpeechConfig,
    SpeechSynthesisOutputFormat,
    SpeechSynthesizer
} from "microsoft-cognitiveservices-speech-sdk";
import {createAudioContext} from "./TextToSpeechUtilsPoly";
import {RecognizedPhrase} from "./RecognizedPhrase";
import PhrasesRegistry from "./PhrasesRegistry";


export class HybridModelText2SpeechProcessor {
    //Services
    private apiKey: string;
    private region = "eastus";
    private speechSynthesizer: SpeechSynthesizer;

    //Config
    private outputConfig: Output;
    private readonly speechConfig: SpeechConfig;

    //Devices
    private audioContext: AudioContext;
    private currentGainNode: GainNode | null = null;
    private audioBufferSourceNode: AudioBufferSourceNode | null = null;

    //Pollers
    private pollingInterval: NodeJS.Timer | undefined = undefined;
    private speakerPollingInterval: NodeJS.Timer | undefined = undefined;

    //State
    private phrasesRegistry: PhrasesRegistry = new PhrasesRegistry();
    private pendingText2SpeechQueue: Queue<string>;
    private pendingSpeakerQueue: Queue<AudioBuffer>;
    private imSpeaking: boolean = false;
    private currentRecognizedTokens: string[] | null = null;
    private isDequeing = false;

    constructor(outputConfig: Output, azureSpeechAPIToken: string) {
        this.outputConfig = outputConfig;
        this.pendingText2SpeechQueue = new Queue<string>();
        this.pendingSpeakerQueue = new Queue<AudioBuffer>();

        this.apiKey = azureSpeechAPIToken;

        console.log('[HybridMode_Text2SpeechProcessor] Creating T2S processor...');
        this.speechConfig = SpeechConfig.fromAuthorizationToken(this.apiKey, this.region);
        /*this.speechConfig.speechSynthesisLanguage = outputConfig.language.speakKey;
        this.speechConfig.speechSynthesisVoiceName = outputConfig.language.voices[0];*/
        this.speechConfig.speechSynthesisOutputFormat = SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3

        this.audioContext = createAudioContext(outputConfig.device.deviceId) as AudioContext;

        //Not sure why; I think this disables default Azure behavior of playing back sound automatically on default device
        //We won't use this stream nor the AudioConfig either, but it is mandatory for creating a Synthesizer
        const pullAudioOutputStream = AudioOutputStream.createPullStream();
        const audioConfig = AudioConfig.fromStreamOutput(pullAudioOutputStream);

        this.speechSynthesizer = new SpeechSynthesizer(this.speechConfig, audioConfig);
        this.warmUpConnection();

        this.currentRecognizedTokens = [];

        console.log('[HybridMode_Text2SpeechProcessor] Ready and connected to Services and Devices');
    }

    private warmUpConnection() {
        console.log('[HybridMode_Text2SpeechProcessor] Warming up connection with dummy text...');
        this.speechSynthesizer.speakTextAsync("Testing...");
    }

    /**
     * Add text to the processor queue.
     * A poller eventually will take this text and perform the following tasks:
     * - Add ssml to the text
     * - Send it to the Text2Speech and get a Speech audio back
     * - Enqueue in the Audio Output the audio
     * @param resultId
     * @param textToSpeak
     * @param isStable
     */
    public enqueueText = (resultId: string, textToSpeak: string, isStable: boolean): void => {
        console.log('[HybridMode_Text2SpeechProcessor] Queueing T2S services...', textToSpeak, resultId, isStable)

        if (this.phrasesRegistry.upsertPhrase(resultId, textToSpeak, isStable)) {
            this.pendingText2SpeechQueue.enqueue(resultId);
        }
    }

    public async dequeueAsync(): Promise<string> {
        console.log('[Dequeue Async] isDequeing?', this.isDequeing)
        const phraseId = this.pendingText2SpeechQueue.peek();
        if (!phraseId) return ''; //Nothing to say / There are no phrases

        const phrase = this.phrasesRegistry.getPhrase(phraseId);
        if (!phrase) return ''; //Nothing to say / There are no phrases

        let nextPhraseToSay = '';

        if (!phrase.state.isCurrentlySpeaking) {
            console.log('[Dequeue Async] Currently, not speaking')
            if (!!phrase.stablePhrase) { //Prioritize speaking stable phrases
                console.log('[Dequeue Async] Returning stable phrase')
                this.pendingText2SpeechQueue.dequeue();
                nextPhraseToSay = phrase.stablePhrase; //nothing to curate
            } else {
                console.log('[Dequeue Async] Will wait 5 secs until stable')
                phrase.state.isCurrentlySpeaking = true;
                return new Promise<string>((resolve, reject) => {
                    setTimeout(() => {
                        nextPhraseToSay = this.solveUseStableOrUnstable(phrase, phraseId);
                        console.log('[Dequeue Async] Returning phrase after 5 secs')
                        resolve(nextPhraseToSay);
                    }, 5000);
                });
            }
        } else {
            console.log('[Dequeue Async] Already speaking')
            phrase.state.isCurrentlySpeaking = true;
            nextPhraseToSay = this.solveUseStableOrUnstable(phrase, phraseId);
        }

        return nextPhraseToSay;
    }

    private solveUseStableOrUnstable(phrase: RecognizedPhrase, id: string) {

        console.log('[Dequeue Async] solveUseStableOrUnstable ')
        let curatedNextPhraseToSay = '';

        if (!!phrase.stablePhrase) { //Prioritize speaking stable phrases
            this.pendingText2SpeechQueue.dequeueObject(id);
            const nextPhraseToSay = phrase.stablePhrase;
            const phraseAlreadySaid = phrase.unstablePhraseFragments?.length > 0 ? phrase.unstablePhraseFragments[phrase.state.unstableFragmentAlreadySaidIndex] : '';
            curatedNextPhraseToSay = RecognizedPhrase.curatePhrase(nextPhraseToSay, phraseAlreadySaid);
            console.log('[Dequeue Async] solveUseStableOrUnstable; returning stable')
        } else { //If there is no stable, use last unstable available
            const phraseAlreadySaid = phrase.unstablePhraseFragments[phrase.state.unstableFragmentAlreadySaidIndex];
            const nextFragmentIndex = phrase.unstablePhraseFragments.length - 1;
            const nextPhraseToSay = phrase.unstablePhraseFragments[nextFragmentIndex] ?? '';
            curatedNextPhraseToSay = RecognizedPhrase.curatePhrase(nextPhraseToSay, phraseAlreadySaid);
            phrase.state.unstableFragmentAlreadySaidIndex = nextFragmentIndex;
            console.log('[Dequeue Async] solveUseStableOrUnstable; returning unstable')
        }
        return curatedNextPhraseToSay;
    }

    /**
     * Enqueue an AudioBuffer to the connected device.
     * A poller will periodically consume this queue and play the audio on the device.
     * @param audioToSpeak
     */
    public sendToSpeakerQueue = (audioToSpeak: AudioBuffer): void => {
        console.log('[Text2SpeechProcessor] Queueing audio to device...', audioToSpeak)
        this.pendingSpeakerQueue.enqueue(audioToSpeak);
    }

    /**
     *
     */
    public start(): void {
        if (this.pollingInterval || this.speakerPollingInterval) {
            throw new Error("[Text2SpeechProcessor] Cant start Text2Speech processor. There's already a polling interval running.");
        }

        this.pollingInterval = setInterval(async () => {
            if (this.imSpeaking || this.isDequeing)
                return;

            // console.log('[Text2SpeechProcessor] dequeing... Queue', this.pendingText2SpeechQueue);

            this.isDequeing = true;
            console.log('Before dequeue ', this.pendingText2SpeechQueue.items);
            let textToSpeak = await this.dequeueAsync();
            console.log('After dequeue ', this.pendingText2SpeechQueue.items);
            this.isDequeing = false;
            console.log('[Text2SpeechProcessor] dequeued', textToSpeak, this.isDequeing);
            if (!!textToSpeak)
                this.speak(textToSpeak, DEFAULT_VOICE_INDEX);

        }, 100)

        this.speakerPollingInterval = setInterval(() => {
            if (this.imSpeaking)
                return;

            // console.log('[Text2SpeechProcessor] dequeing... Queue', this.pendingText2SpeechQueue);
            let audioToSpeak = this.pendingSpeakerQueue.dequeue();
            // console.log('[Text2SpeechProcessor] dequeued', textToSpeak);
            if (!!audioToSpeak)
                this.playOnDevice(audioToSpeak);

        }, 100)
    }

    public stop(): void {
        clearInterval(this.pollingInterval);
        clearInterval(this.speakerPollingInterval);
        this.speechSynthesizer.close();
        this.currentGainNode?.disconnect();
        this.audioBufferSourceNode?.disconnect();
        this.imSpeaking = false;
    }

    public async setOutputVolume(gain: number) {
        if (this.outputConfig.textOnly) return;

        if (!this.audioContext) {
            console.log('No audio context')
            return;
        }

        this.outputConfig.gain = gain;
        if (this.currentGainNode)
            this.currentGainNode.gain.setValueAtTime(this.outputConfig.gain, this.audioContext.currentTime);
    }

    public async setOutputPace(pace: number) {
        this.outputConfig.pace = pace;
    }

    public getOutputConfig(): Output {
        return this.outputConfig;
    }

    private buildSsml(textToSpeak: string,
                      lang: string,
                      voice: string,
                      pace: number) {
        const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${lang}"><voice name="${voice}"><prosody rate="${pace}">${textToSpeak}</prosody></voice></speak>`;
        console.log('Build SSML: ', ssml);
        return ssml;
    }

    private playOnDevice(buffer: AudioBuffer) {
        if (this.outputConfig.textOnly) return;

        if (!this.audioContext) {
            console.log('No audio context')
            return;
        }


        this.imSpeaking = true;

        this.currentGainNode = this.audioContext.createGain();
        this.currentGainNode.gain.setValueAtTime(this.outputConfig.gain, this.audioContext.currentTime);

        this.audioBufferSourceNode = this.audioContext.createBufferSource();
        this.audioBufferSourceNode.buffer = buffer;
        this.audioBufferSourceNode.onended = (event) => {
            this.currentGainNode?.disconnect();
            this.audioBufferSourceNode?.disconnect();
            this.imSpeaking = false;
            //onSpeakComplete();
        }

        this.audioBufferSourceNode.connect(this.currentGainNode);
        this.currentGainNode.connect(this.audioContext.destination);
        console.log('[HybridMode_Text2SpeechProcessor] audioBufferSourceNode start');
        this.audioBufferSourceNode.start();
    }

    private speak(textToSpeak: string, voiceIndex: number) {

        const sendToDevice = (data: ArrayBuffer) => {
            if (this.outputConfig.textOnly) return;

            if (!this.audioContext) {
                console.log('No audio context')
                return;
            }

            this.audioContext.decodeAudioData(data, (buffer) => {
                this.sendToSpeakerQueue(buffer);
            }, error => {
                console.log('[HybridMode_Text2SpeechProcessor] err', error)
            });
        }

        const ssml = this.buildSsml(textToSpeak, this.outputConfig.language.speakKey, this.outputConfig.language.voices[voiceIndex], this.outputConfig.pace);

        this.speechSynthesizer.speakSsmlAsync(ssml, (speechResult => {
            console.log('[HybridMode_Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            sendToDevice(speechResult.audioData);
        }), err => {
            console.log('[HybridMode_Text2SpeechProcessor] Speech error', err);
        });

        /*this.speechSynthesizer.speakTextAsync(textToSpeak, (speechResult => {
            console.log('[Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            playOnDevice(speechResult.audioData);
        }), err => {
            console.log('[Text2SpeechProcessor] Speech error', err);
        });*/
    }

    public setSpeechToken(newToken: string) {
        this.speechSynthesizer.authorizationToken = newToken;
    }

}
